import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getLive, buyLiveTicket, joinLive, leaveLive, startLive,
  listViewers, inviteGuest, respondInvite, removeGuest, deleteLive,
  bookFreeLive, respondCohostRequest,
} from '../api/lives';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import TipJar from '../components/TipJar';
import CohostRequestButton from '../components/CohostRequestButton';
import CohostRequestsPanel from '../components/CohostRequestsPanel';
import LiveKitBroadcastRoom, { endLiveSession, toggleMicSession, getMicSessionState, subscribeMicSession } from '../components/LiveKitBroadcastRoom';
import LiveKitViewer from '../components/LiveKitViewer';
import LiveChat from '../components/LiveChat';
import { blockUser } from '../api/blocks';
import { useCategories } from '../api/categories';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Lock, Ticket, Calendar, Clock, Video, Mic, MicOff, Users, UserPlus,
  X, Check, ArrowLeft, Sparkles, Trash2, Play, Square, Tag, Globe,
} from 'lucide-react';
import { mediaUrl } from '../api/posts';
import { fmtCredits } from '../lib/currency';
import InsufficientBalanceDialog from '../components/InsufficientBalanceDialog';
import useHostCohostEvents from '../hooks/useHostCohostEvents';
import { LiveScheduleCountdown } from '../components/LiveScheduleCountdown';

const fmtDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
};

// Phase 28 — render the creator's local time as a small companion tooltip so
// the viewer can mentally double-check the time zone of the broadcaster.
const fmtDateInTz = (iso, tz) => {
  if (!iso || !tz) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: 'short', day: 'numeric', month: 'short',
      hour: '2-digit', minute: '2-digit', timeZone: tz, timeZoneName: 'short',
    });
  } catch { return ''; }
};

// Phase 28 — Tailwind aspect class from the creator's chosen video_format.
const aspectClassFor = (fmt) => {
  if (fmt === '9:16') return 'aspect-[9/16] mx-auto max-w-md';
  if (fmt === '1:1') return 'aspect-square mx-auto max-w-2xl';
  return 'aspect-video';
};

// Phase 27 — human-readable label for live kind.
const kindLabel = (kind) => (kind === 'audio' ? 'Solo Audio' : 'Video + Audio');

const Avatar = ({ src, name, size = 36 }) => (
  src ? (
    <img src={src} alt={name} className="shrink-0 rounded-full object-cover object-center" style={{ width: size, height: size, minWidth: size }} />
  ) : (
    <div
      className="flex shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
      style={{ width: size, height: size }}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  )
);

// Phase 27 — Booking / Purchase CTA card. Placed prominently under the
// title to act as the landing-page hero. Renders different states:
//  - already booked / has paid ticket -> green "Prenotato" badge
//  - sold out (max_participants reached) -> disabled "Posti esauriti"
//  - free live -> "Prenota gratuitamente"
//  - paid live -> "Acquista accesso – X Credits"
const BookingCtaCard = ({ live, busy, isSoldOut, onBookFree, onBuyTicket }) => {
  const isPaid = live.is_paid;
  const alreadyHas = isPaid ? live.has_ticket : live.is_booked;
  const cap = live.max_participants;
  const sold = Number(live.tickets_sold || 0);
  const seatsLeft = cap ? Math.max(0, Number(cap) - sold) : null;
  const progress = cap ? Math.min(100, Math.round((sold / Number(cap)) * 100)) : 0;

  return (
    <div
      className="mt-4 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-card to-card p-4 shadow-sm"
      data-testid="live-detail-cta-card"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            {isPaid ? 'Live a pagamento' : 'Live gratuita su prenotazione'}
          </p>
          <p className="mt-1 text-2xl font-semibold gold-text">
            {isPaid ? fmtCredits(live.price) : 'Gratis'}
          </p>
          {cap ? (
            <div className="mt-2 max-w-xs" data-testid="live-detail-seats-progress">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{sold} / {cap} prenotati</span>
                <span>{seatsLeft} {seatsLeft === 1 ? 'posto' : 'posti'} disponibili</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-secondary">
                <div
                  className={`h-full ${progress >= 100 ? 'bg-red-500' : 'bg-amber-500'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              <Users className="mr-1 inline h-3 w-3" /> {sold} prenotati · posti illimitati
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center">
          {alreadyHas ? (
            <Button
              disabled
              className="flex items-center gap-2 rounded-full bg-emerald-500/90 px-5 py-2.5 text-sm font-semibold text-white"
              data-testid="live-detail-booked-badge"
            >
              <Check className="h-4 w-4" /> {isPaid ? 'Accesso acquistato' : 'Prenotato'}
            </Button>
          ) : isSoldOut ? (
            <Button
              disabled
              className="flex items-center gap-2 rounded-full bg-red-500/90 px-5 py-2.5 text-sm font-semibold text-white"
              data-testid="live-detail-soldout-btn"
            >
              <Lock className="h-4 w-4" /> Posti esauriti
            </Button>
          ) : isPaid ? (
            <Button
              onClick={onBuyTicket}
              disabled={busy}
              className="gold-gradient flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-black"
              data-testid="live-detail-paid-book-btn"
            >
              <Ticket className="h-4 w-4" />
              {busy ? 'Caricamento…' : `Acquista accesso – ${fmtCredits(live.price)}`}
            </Button>
          ) : (
            <Button
              onClick={onBookFree}
              disabled={busy}
              className="gold-gradient flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-black"
              data-testid="live-detail-book-btn"
            >
              <Ticket className="h-4 w-4" />
              {busy ? 'Prenotazione…' : 'Prenota gratuitamente'}
            </Button>
          )}
        </div>
      </div>
      {isPaid && !alreadyHas && !isSoldOut && (
        <p className="mt-3 text-[11px] text-muted-foreground">
          Pagamento sicuro — il posto è riservato solo dopo la conferma del pagamento.
        </p>
      )}
    </div>
  );
};

const GuestTile = ({ guest, audioOnly, onRemove, isHost }) => (
  <div className="relative aspect-square overflow-hidden rounded-xl bg-gradient-to-br from-neutral-800 to-neutral-900 ring-1 ring-white/10">
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
      <div className="rounded-full bg-gradient-to-tr from-pink-500 via-amber-400 to-red-500 p-[3px]">
        <div className="rounded-full bg-neutral-900 p-[2px]">
          <Avatar src={guest.avatar_url} name={guest.username} size={56} />
        </div>
      </div>
      <div className="text-center">
        <p className="text-xs font-semibold">@{guest.username}</p>
        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-200">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-400" /> {audioOnly ? 'In onda' : 'Sul palco'}
        </span>
      </div>
    </div>
    {isHost && (
      <button
        onClick={onRemove}
        className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
        aria-label="Rimuovi partecipante"
        title="Rimuovi partecipante"
        data-testid={`cohost-remove-${guest.user_id}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

const LiveDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { nameOf } = useCategories();
  const { t } = useTranslation('lives');
  const { t: tCommon } = useTranslation('common');
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null); // 'end' | 'delete' | null
  // Phase 83 — page-level mic state (true=on, false=muted, null=no track yet).
  const [hostMicOn, setHostMicOn] = useState(null);
  // Phase 28 — live chat object (messages/send/deleteMessage) bubbled up
  // from the inner LiveKitBroadcastRoom or LiveKitViewer.
  const [liveChat, setLiveChat] = useState(null);
  const [insufficient, setInsufficient] = useState(false);
  const joinedRef = useRef(false);

  // Phase 83 — mirror the broadcast session mic state (initial + updates).
  useEffect(() => {
    if (!live?.is_owner || live?.status !== 'live') { setHostMicOn(null); return undefined; }
    setHostMicOn(getMicSessionState(id));
    return subscribeMicSession(id, setHostMicOn);
  }, [id, live?.is_owner, live?.status]);

  const refresh = useCallback(async () => {
    try {
      const d = await getLive(id);
      setLive(d);
      return d;
    } catch (e) {
      toast({ title: 'Live not found', variant: 'destructive' });
      navigate('/lives');
      return null;
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
    const poll = setInterval(refresh, 5000);
    return () => clearInterval(poll);
  }, [refresh]);

  // Giu 2026 — host realtime co-host requests (SSE + bell), server-authoritative.
  useHostCohostEvents({
    liveId: id,
    enabled: !!(live?.is_owner && live?.status === 'live'),
    onChanged: refresh,
  });

  // Join when live starts and we have access
  useEffect(() => {
    if (!live) return;
    if (live.is_owner) return;
    if (live.status !== 'live') return;
    if (!live.has_ticket) return;
    if (joinedRef.current) return;
    joinedRef.current = true;
    joinLive(id).catch(() => {});
    return () => {
      if (joinedRef.current) {
        leaveLive(id).catch(() => {});
        joinedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, live?.status, live?.has_ticket, live?.is_owner]);

  // Host: load viewers list periodically when live
  useEffect(() => {
    if (!live?.is_owner || live.status !== 'live') return;
    let alive = true;
    const load = async () => {
      try {
        const v = await listViewers(id);
        if (alive) setViewers(v.viewers);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [id, live?.is_owner, live?.status]);

  const buyTicket = async () => {
    if (!user) {
      toast({ title: 'Accedi per prenotare', description: 'Effettua il login per acquistare il biglietto.' });
      navigate('/login');
      return;
    }
    setBusy(true);
    try {
      await buyLiveTicket(id);
      toast({
        title: 'Ticket acquistato',
        description: `Hai pagato ${fmtCredits(live?.price || 0)} dal Wallet.`,
      });
      try { window.dispatchEvent(new CustomEvent('nymia:data-changed')); } catch (_) { /* noop */ }
      await refresh();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 402 && detail.startsWith('insufficient_balance')) {
        setInsufficient(true);
      } else {
        toast({
          title: 'Acquisto non riuscito',
          description: detail.split('|')[1] || detail || '',
          variant: 'destructive',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  // Phase 23.1 — free booking for free lives.
  const bookFree = async () => {
    if (!user) {
      toast({ title: 'Accedi per prenotare', description: 'Effettua il login per prenotare la live.' });
      navigate('/login');
      return;
    }
    setBusy(true);
    try {
      const updated = await bookFreeLive(id);
      setLive(updated);
      toast({ title: 'Prenotazione confermata', description: 'Ti sei prenotato per questa live.' });
    } catch (err) {
      toast({
        title: 'Prenotazione non riuscita',
        description: err.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const doStart = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const started = await startLive(id);
      setLive(started);
      toast({ title: t(started.status === 'live' ? 'schedule.status_live' : 'schedule.starting') });
    } catch (err) {
      toast({ title: 'Could not start', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };
  const performEnd = async () => {
    // Phase 76 — single centralized end flow (endLiveSession): waits for the
    // backend confirmation, then stops camera/mic and closes the LiveKit
    // room locally. No more fire-and-forget races.
    if (busy) return;
    setBusy(true);
    try {
      await endLiveSession(id);
      await refresh();
      toast({ title: 'Live terminata' });
    } catch (err) {
      toast({ title: 'Impossibile terminare la live', description: err.response?.data?.detail || 'Riprova.', variant: 'destructive' });
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  };
  const performDelete = async () => {
    setBusy(true);
    try {
      await deleteLive(id);
      toast({ title: 'Live deleted' });
      navigate('/lives');
    } catch (err) {
      toast({ title: 'Could not delete', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  };
  const doInvite = async (userId) => {
    try {
      await inviteGuest(id, userId);
      console.info('[GUEST] invite sent to', userId);
      toast({ title: t('guest.invite_sent') });
      await refresh();
    } catch (err) {
      toast({ title: t('guest.invite_failed'), variant: 'destructive' });
    }
  };
  const doRemoveGuest = async (userId) => {
    try {
      await removeGuest(id, userId);
      await refresh();
    } catch { /* ignore */ }
  };
  const doRespondInvite = async (accept) => {
    try {
      await respondInvite(id, accept);
      console.info('[GUEST] invite', accept ? 'accepted' : 'declined');
      toast({ title: accept ? t('guest.on_stage_now') : t('guest.invite_declined') });
      await refresh();
    } catch (err) {
      toast({ title: t('guest.respond_failed'), variant: 'destructive' });
    }
  };
  // Phase 53 · Creator decides a viewer's co-host request straight from
  // the on-stage banner (no page refresh, works on mobile too).
  const [requestBusy, setRequestBusy] = useState(false);
  const decideRequest = async (userId, accept) => {
    setRequestBusy(true);
    try {
      await respondCohostRequest(id, userId, accept);
      console.info('[GUEST] request', accept ? 'accepted' : 'declined', 'for', userId);
      toast({ title: accept ? t('guest.request_accepted') : t('guest.request_declined') });
      await refresh();
    } catch (_e) {
      toast({ title: t('guest.respond_failed'), variant: 'destructive' });
    } finally { setRequestBusy(false); }
  };

  const onStageGuests = useMemo(() => (live?.guests || []).filter((g) => g.status === 'on_stage'), [live]);
  const pendingGuests = useMemo(() => (live?.guests || []).filter((g) => g.status === 'pending'), [live]);
  const requestedGuests = useMemo(() => (live?.guests || []).filter((g) => g.status === 'requested'), [live]);

  // Phase 53 · Instant creator notification — the 5s live poll delivers
  // new co-host requests; the moment a NEW one appears we fire a toast
  // (plus the persistent banner below). No page refresh needed.
  const seenRequestsRef = useRef(new Set());
  useEffect(() => {
    if (!live?.is_owner || live.status !== 'live') return;
    const seen = seenRequestsRef.current;
    requestedGuests.forEach((g) => {
      if (!seen.has(g.user_id)) {
        seen.add(g.user_id);
        console.info('[GUEST] request received by creator from @' + g.username);
        toast({
          title: t('guest.request_toast_title'),
          description: t('guest.request_toast_desc', { username: g.username }),
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedGuests, live?.is_owner, live?.status]);

  // Phase 53 · debug log — invite received on the viewer side.
  const invitedLoggedRef = useRef(false);
  useEffect(() => {
    if (live?.pending_invite && !invitedLoggedRef.current) {
      invitedLoggedRef.current = true;
      console.info('[GUEST] invite received from @' + (live.creator?.username || 'host'));
    }
    if (!live?.pending_invite) invitedLoggedRef.current = false;
  }, [live?.pending_invite, live?.creator?.username]);

  if (loading || !live) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-neutral-800" />
      </div>
    );
  }

  const audioOnly = live.kind === 'audio';
  // Phase 35.B — the creator must be able to publish during waiting_tracks
  // so the anti-fraud watchdog can verify the broadcast.
  const isWaitingTracks = live.status === 'waiting_tracks';
  const isCancelledAuto = typeof live.status === 'string' && live.status.startsWith('cancelled');
  const canWatch = (live.has_ticket || live.is_owner) && (live.status === 'live' || (live.is_owner && isWaitingTracks));
  const needsTicket = live.is_paid && !live.has_ticket && !live.is_owner;

  // Phase 35.B — human-readable explanations for the new cancellation states.
  const cancelReasonLabel = (() => {
    switch (live.cancelled_reason) {
      case 'creator_no_show': return 'Il creator non è arrivato in tempo per l\'avvio.';
      case 'no_audio_detected': return 'Audio non rilevato.';
      case 'no_video_audio_detected': return 'Audio e video non rilevati.';
      case 'livekit_publish_failed': return 'Errore di pubblicazione delle tracce.';
      case 'technical_failure': return 'Errore tecnico durante l\'avvio.';
      default: return live.status === 'cancelled' ? 'Live annullata.' : 'Live annullata automaticamente.';
    }
  })();

  return (
    <div className="mx-auto max-w-[1100px] px-4 pt-4 pb-24 md:pt-8">
      <button
        type="button"
        onClick={() => {
          if (window.history.state?.idx > 0) navigate(-1);
          else navigate(live.is_owner ? '/create?mode=schedule' : '/', { replace: true });
        }}
        aria-label={tCommon('back')}
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        data-testid="live-back-btn"
      >
        <ArrowLeft className="h-4 w-4" /> {tCommon('back')}
      </button>

      {/* Pending guest invite banner — Phase 53: fully localized (IT/EN). */}
      {live.pending_invite && (
        <div
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-pink-200 bg-gradient-to-r from-pink-50 to-amber-50 px-4 py-3"
          data-testid="guest-invite-banner"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-pink-500 text-white">
              <UserPlus className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-900">{t('guest.invited_title')}</p>
              <p className="text-xs text-neutral-600">
                {t('guest.invited_desc', { username: live.creator?.username })}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => doRespondInvite(false)}
              variant="ghost"
              className="text-neutral-800 hover:bg-black/5"
              data-testid="guest-invite-decline-btn"
            >
              {t('guest.decline')}
            </Button>
            <Button
              onClick={() => doRespondInvite(true)}
              className="bg-pink-500 text-white hover:bg-pink-600"
              data-testid="guest-invite-accept-btn"
            >
              <Check className="mr-1 h-4 w-4" /> {t('guest.accept')}
            </Button>
          </div>
        </div>
      )}

      {/* Phase 53 · Creator-side co-host request banner ("popup"). Shows the
          oldest pending request with inline Accept/Decline — visible on
          mobile too (the sidebar panel sits below the fold there). */}
      {live.is_owner && live.status === 'live' && requestedGuests.length > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300/60 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3"
          data-testid="cohost-request-banner"
        >
          <div className="flex items-center gap-3">
            <Avatar src={requestedGuests[0].avatar_url} name={requestedGuests[0].username} size={36} />
            <div>
              <p className="text-sm font-semibold text-neutral-900">
                {t('guest.request_banner_title', { username: requestedGuests[0].username })}
              </p>
              {requestedGuests[0].message ? (
                <p className="text-xs italic text-neutral-600">“{requestedGuests[0].message}”</p>
              ) : (
                <p className="text-xs text-neutral-600">{t('guest.request_banner_desc')}</p>
              )}
              {requestedGuests.length > 1 && (
                <p className="mt-0.5 text-[11px] font-medium text-amber-700">
                  {t('guest.request_banner_more', { count: requestedGuests.length - 1 })}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => decideRequest(requestedGuests[0].user_id, false)}
              disabled={requestBusy}
              variant="ghost"
              className="text-neutral-800 hover:bg-black/5"
              data-testid="cohost-banner-decline-btn"
            >
              <X className="mr-1 h-4 w-4" /> {t('guest.decline')}
            </Button>
            <Button
              onClick={() => decideRequest(requestedGuests[0].user_id, true)}
              disabled={requestBusy}
              className="bg-emerald-500 text-white hover:bg-emerald-600"
              data-testid="cohost-banner-accept-btn"
            >
              <Check className="mr-1 h-4 w-4" /> {t('guest.accept')}
            </Button>
          </div>
        </div>
      )}

      {/* Phase 35.B — anti-fraud cancellation banner (user + creator copy). */}
      {isCancelledAuto && (
        <div
          className="mb-4 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm"
          data-testid="live-cancelled-banner"
        >
          <p className="font-semibold text-red-200">
            {live.is_owner ? 'La tua Live è stata annullata' : 'Live annullata'}
          </p>
          <p className="mt-1 text-red-100/80">{cancelReasonLabel}</p>
          {live.is_paid && !live.is_owner && (
            <p className="mt-2 text-xs text-red-100/70">
              I crediti sono stati rimborsati automaticamente nel tuo Wallet.
            </p>
          )}
          {live.is_owner && (
            <p className="mt-2 text-xs text-red-100/70">
              Crediti rimborsati automaticamente agli utenti.
              Questa live è stata registrata nello storico affidabilità del tuo account.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
        {/* Stage */}
        <div>
          <div className={`relative overflow-hidden rounded-2xl bg-black ${
            audioOnly ? 'aspect-[5/3]' : aspectClassFor(live.video_format)
          }`}>
            {canWatch && live.is_owner ? (
              /* Host owns the live AND it's live → broadcaster (camera preview) only. */
              <LiveKitBroadcastRoom live={live} onEnded={refresh} embedded onChat={setLiveChat} />
            ) : canWatch ? (
              /* Viewer (booked / paid / on-stage) → subscriber.
                 Phase 27.7 — When my_cohost_status==='on_stage' the viewer
                 also publishes its local mic+camera on the same Room
                 (auto-reconnect with the canPublish token). */
              <LiveKitViewer live={live} cohostStatus={live.my_cohost_status} onChat={setLiveChat} onStageChanged={refresh} onLiveEnded={refresh} />
            ) : isWaitingTracks ? (
              /* Phase 35.B — anti-fraud waiting state. Viewer sees a
                 friendly "waiting for broadcast" placeholder; the creator
                 would have hit `canWatch` above and is publishing. */
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-amber-900 via-amber-800 to-orange-900 px-6 text-white"
                data-testid="live-waiting-tracks"
              >
                <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-amber-300/15 ring-1 ring-amber-200/30 backdrop-blur">
                  <span className="absolute h-full w-full animate-ping rounded-full bg-amber-300/30" />
                  {audioOnly ? <Mic className="h-7 w-7" /> : <Video className="h-7 w-7" />}
                </div>
                <p className="relative text-center text-xs uppercase tracking-widest text-amber-100/80">In avvio</p>
                <p className="relative text-center text-lg font-semibold">In attesa dell&apos;avvio della diretta…</p>
                <p className="relative text-center text-xs text-amber-100/70">
                  La Voce Pro si sta collegando. Resta qui — la live partirà appena le tracce saranno verificate.
                </p>
              </div>
            ) : isCancelledAuto ? (
              /* Phase 35.B — stage placeholder when the live was auto-cancelled. */
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-red-900 to-red-950 px-6 text-white"
                data-testid="live-cancelled-stage"
              >
                <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20 ring-1 ring-red-200/30">
                  <X className="h-7 w-7" />
                </div>
                <p className="relative text-center text-xs uppercase tracking-widest text-red-100/70">Live annullata</p>
                <p className="relative text-center text-lg font-semibold">{cancelReasonLabel}</p>
              </div>
            ) : needsTicket ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-violet-900 via-purple-800 to-fuchsia-900 px-6 text-white">
                {live.cover_url && (
                  <img
                    src={mediaUrl(live.cover_url)}
                    alt={live.title}
                    className="absolute inset-0 h-full w-full object-cover opacity-40"
                  />
                )}
                <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-card/15 ring-1 ring-white/30 backdrop-blur">
                  <Lock className="h-7 w-7" />
                </div>
                <p className="relative text-center text-xs uppercase tracking-widest text-white/70">Live a pagamento {audioOnly ? '· solo audio' : ''}</p>
                <p className="relative text-center text-lg font-semibold">{live.title}</p>
                {live.status === 'scheduled' && (
                  <LiveScheduleCountdown scheduledAt={live.scheduled_at} testId="live-detail-countdown" className="relative text-sm text-white/80" />
                )}
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-neutral-800 to-neutral-900 px-6 text-white">
                {live.cover_url && (
                  <img
                    src={mediaUrl(live.cover_url)}
                    alt={live.title}
                    className="absolute inset-0 h-full w-full object-cover opacity-50"
                  />
                )}
                <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-card/15">
                  {audioOnly ? <Mic className="h-7 w-7" /> : <Video className="h-7 w-7" />}
                </div>
                <p className="relative text-center text-lg font-semibold">{live.title}</p>
                {live.status === 'scheduled' ? (
                  <>
                    <LiveScheduleCountdown scheduledAt={live.scheduled_at} isOwner={live.is_owner} testId="live-detail-countdown" className="relative text-sm text-white/80" />
                    <p className="relative text-xs text-white/60">{fmtDate(live.scheduled_at)}</p>
                  </>
                ) : (
                  <p className="relative text-sm text-white/70">Questa live è terminata.</p>
                )}
              </div>
            )}

            {live.status === 'live' && (
              <div className="absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-full bg-red-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-card" /> LIVE
              </div>
            )}
            {live.status === 'live' && (
              <div className="absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-xs font-semibold text-white" data-testid="live-participants-now">
                <Users className="h-3.5 w-3.5" /> {t('participants_now')}: {live.viewers_count}
              </div>
            )}
          </div>

          {/* Guest tiles */}
          {onStageGuests.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-3">
              {onStageGuests.map((g) => (
                <GuestTile
                  key={g.user_id}
                  guest={g}
                  audioOnly={audioOnly}
                  isHost={live.is_owner}
                  onRemove={() => doRemoveGuest(g.user_id)}
                />
              ))}
            </div>
          )}

          {/* Title block */}
          <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold">{live.title}</h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 font-medium text-foreground/80"
                  data-testid="live-detail-kind-chip"
                >
                  {audioOnly ? <Mic className="h-3 w-3" /> : <Video className="h-3 w-3" />} {kindLabel(live.kind)}
                </span>
                {live.category && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 font-medium text-foreground/80"
                    data-testid="live-detail-category-chip"
                  >
                    <Tag className="h-3 w-3" /> {nameOf(live.category) || live.category}
                  </span>
                )}
                {live.privacy === 'private' && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 font-semibold text-emerald-500"
                    data-testid="live-detail-privacy-chip"
                  >
                    <Lock className="h-3 w-3" /> Privata
                  </span>
                )}
                {live.privacy !== 'private' && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 font-semibold text-emerald-700"
                    data-testid="live-detail-privacy-chip"
                  >
                    <Globe className="h-3 w-3" /> Pubblica
                  </span>
                )}
                <span
                  className="inline-flex items-center gap-1"
                  title={live.creator_timezone ? `Orario originale: ${fmtDateInTz(live.scheduled_at, live.creator_timezone)}` : ''}
                >
                  <Calendar className="h-3 w-3" /> {fmtDate(live.scheduled_at)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {live.duration_minutes}m
                </span>
                <span className="inline-flex items-center gap-1" data-testid="live-detail-bookings-stat">
                  <Users className="h-3 w-3" />
                  {live.max_participants
                    ? `${live.tickets_sold} / ${live.max_participants} prenotati`
                    : `${live.tickets_sold} prenotati`}
                </span>
                {live.is_paid && (
                  <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                    <Sparkles className="h-3 w-3" /> {fmtCredits(live.price)}
                  </span>
                )}
              </div>
            </div>
            {live.is_owner && live.status === 'scheduled' && (
              <Button
                onClick={doStart}
                disabled={busy}
                className="bg-red-500 hover:bg-red-600"
                data-testid="live-go-live-btn"
              >
                <Play className="mr-1.5 h-4 w-4" /> {t('schedule.start')}
              </Button>
            )}
            {live.is_owner && isWaitingTracks && (
              /* Phase 35.B — waiting_tracks: creator is publishing, button
                 shows a non-interactive "in avvio" hint. */
              <div
                className="inline-flex items-center gap-2 rounded-md bg-amber-500/15 px-3 py-1.5 text-xs font-semibold text-amber-200 ring-1 ring-amber-400/30"
                data-testid="live-waiting-tracks-hint"
              >
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-400" />
                Avvio in corso… (max 60s)
              </div>
            )}
            {live.is_owner && live.status === 'live' && (
              <>
                <Button
                  onClick={async () => { await toggleMicSession(id); }}
                  disabled={busy || hostMicOn === null}
                  variant="secondary"
                  className={
                    hostMicOn === false
                      ? 'bg-red-500/15 text-red-500 ring-1 ring-red-500/40 hover:bg-red-500/25'
                      : 'bg-neutral-800 text-white hover:bg-neutral-700'
                  }
                  title={hostMicOn === false ? 'Riattiva il microfono' : 'Metti in muto il microfono'}
                  aria-pressed={hostMicOn === false}
                  data-testid="live-page-mic-btn"
                >
                  {hostMicOn === false ? <MicOff className="mr-1.5 h-4 w-4" /> : <Mic className="mr-1.5 h-4 w-4" />}
                  {hostMicOn === false ? 'Muto' : 'Mic'}
                </Button>
                <Button onClick={() => setConfirmAction('end')} disabled={busy} variant="destructive" className="bg-red-600 text-white hover:bg-red-700" data-testid="live-end-btn">
                  <Square className="mr-1.5 h-4 w-4" /> Termina live
                </Button>
              </>
            )}
            {live.is_owner && live.status === 'scheduled' && (
              <Button onClick={() => setConfirmAction('delete')} disabled={busy} variant="ghost" className="text-red-600 hover:bg-red-50">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Phase 27 — Booking / Purchase CTA card (landing-page hero). Only
              shown for scheduled lives to non-owners. Buttons mirror the
              wording from the spec: "Prenota gratuitamente" / "Acquista
              accesso". After successful action a green "Prenotato" badge
              replaces the CTA. Owners and ended/live states don't show this
              block. */}
          {!live.is_owner && live.status === 'scheduled' && (
            <BookingCtaCard
              live={live}
              busy={busy}
              isSoldOut={Boolean(live.max_participants) && (live.tickets_sold || 0) >= Number(live.max_participants)}
              onBookFree={bookFree}
              onBuyTicket={buyTicket}
            />
          )}

          {/* Phase 24.E.3 — broadcast room is rendered INSIDE the main stage
              when host is owner+live (see above). No duplicate player. */}

          {/* Creator + description */}
          <button
            onClick={() => live.creator?.username && navigate(`/u/${live.creator.username}`)}
            className="mt-4 flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left hover:bg-background"
          >
            <Avatar src={live.creator?.avatar_url} name={live.creator?.username} size={44} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">@{live.creator?.username}</p>
              <p className="truncate text-xs text-muted-foreground">{live.creator?.full_name}</p>
            </div>
          </button>
          {live.description && (
            <p className="mt-3 whitespace-pre-wrap text-sm text-foreground">{live.description}</p>
          )}
        </div>

        {/* Side panel */}
        <aside className="space-y-4">
          {live.status === 'live' && (
            <TipJar live={live} onTipSent={() => refresh()} />
          )}
          {live.status === 'live' && !live.is_owner && (
            <CohostRequestButton live={live} onChanged={() => refresh()} />
          )}
          {live.is_owner && live.status === 'live' && (
            <CohostRequestsPanel live={live} onChanged={() => refresh()} />
          )}
          {live.is_owner && live.status === 'live' && (
            <div className="rounded-xl border border-border bg-card">
              <header className="flex items-center justify-between border-b border-border px-4 py-2">
                <h3 className="flex items-center gap-1.5 text-sm font-semibold">
                  <Users className="h-4 w-4" /> Spettatori
                </h3>
                <span className="text-xs text-muted-foreground">{viewers.length}</span>
              </header>
              {viewers.length === 0 ? (
                <p className="px-4 py-6 text-center text-xs text-muted-foreground">Ancora nessuno spettatore</p>
              ) : (
                <ul className="max-h-72 overflow-y-auto divide-y divide-border/60">
                  {viewers.map((v) => (
                    <li key={v.id} className="flex items-center justify-between px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Avatar src={v.avatar_url} name={v.username} size={32} />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">@{v.username}</p>
                          <p className="truncate text-[11px] text-muted-foreground">{v.full_name}</p>
                        </div>
                      </div>
                      {v.is_guest ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
                          sul palco
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => doInvite(v.id)}
                          className="h-7 bg-pink-500 px-2 text-xs hover:bg-pink-600"
                          data-testid={`invite-viewer-${v.id}`}
                        >
                          <UserPlus className="mr-1 h-3 w-3" /> Invita
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {pendingGuests.length > 0 && (
                <div className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
                  {pendingGuests.length} {pendingGuests.length > 1 ? 'inviti in attesa' : 'invito in attesa'}: {pendingGuests.map((g) => '@' + g.username).join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Phase 28 — Live chat sidebar (visible only when broadcast is live and viewer can watch). */}
          {live.status === 'live' && canWatch && liveChat && (
            <LiveChat
              messages={liveChat.messages}
              send={liveChat.send}
              deleteMessage={liveChat.deleteMessage}
              currentUser={user}
              isCreator={live.is_owner}
              onBanUser={live.is_owner ? blockUser : null}
              variant="sidebar"
              placeholder={live.is_owner ? 'Saluta il pubblico…' : 'Scrivi nella chat…'}
            />
          )}

          {/* Phase 27 — Sintesi della live (sidebar landing). */}
          <div className="rounded-xl border border-border bg-card p-4" data-testid="live-detail-summary">
            <h3 className="mb-3 text-sm font-semibold">Dettagli evento</h3>
            <dl className="space-y-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <dt className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" /> Data e ora
                </dt>
                <dd className="text-right font-medium text-foreground">{fmtDate(live.scheduled_at)}</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" /> Durata
                </dt>
                <dd className="font-medium text-foreground">{live.duration_minutes} minuti</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="inline-flex items-center gap-1.5 text-muted-foreground">
                  {audioOnly ? <Mic className="h-3.5 w-3.5" /> : <Video className="h-3.5 w-3.5" />} Formato
                </dt>
                <dd className="font-medium text-foreground">{kindLabel(live.kind)}</dd>
              </div>
              {live.category && (
                <div className="flex items-center justify-between gap-2">
                  <dt className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <Tag className="h-3.5 w-3.5" /> Categoria
                  </dt>
                  <dd className="font-medium text-foreground">{nameOf(live.category) || live.category}</dd>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <dt className="inline-flex items-center gap-1.5 text-muted-foreground">
                  {live.privacy === 'private' ? <Lock className="h-3.5 w-3.5" /> : <Globe className="h-3.5 w-3.5" />} Visibilità
                </dt>
                <dd className="font-medium text-foreground">{live.privacy === 'private' ? 'Privata' : 'Pubblica'}</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Users className="h-3.5 w-3.5" /> Prenotati
                </dt>
                <dd className="font-medium text-foreground">
                  {live.max_participants ? `${live.tickets_sold} / ${live.max_participants}` : live.tickets_sold}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Ticket className="h-3.5 w-3.5" /> Accesso
                </dt>
                <dd className="font-medium text-foreground">
                  {live.is_paid ? `A pagamento – ${fmtCredits(live.price)}` : 'Gratuito'}
                </dd>
              </div>
            </dl>
            <p className="mt-3 border-t border-border pt-3 text-[11px] text-muted-foreground">
              Diretta WebRTC in tempo reale dal browser dello smartphone. Nessuna app esterna richiesta.
            </p>
          </div>
        </aside>
      </div>

      <AlertDialog open={confirmAction !== null} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === 'end' ? 'Terminare la live?' : 'Eliminare questa live?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === 'end'
                ? 'I tuoi spettatori saranno disconnessi e la trasmissione si interromperà. Non potrai riprenderla dopo averla terminata.'
                : 'Questa live verrà rimossa definitivamente e gli eventuali biglietti venduti resteranno nello storico del tuo wallet.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={confirmAction === 'end' ? performEnd : performDelete}
              data-testid="live-confirm-action-btn"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {busy ? 'Attendere…' : confirmAction === 'end' ? 'Termina live' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <InsufficientBalanceDialog
        open={insufficient}
        onOpenChange={setInsufficient}
        balance={Number(user?.wallet_balance || 0)}
        required={Number(live?.price || 0)}
        context="ticket"
      />
    </div>
  );
};

export default LiveDetailPage;
